import React, { useState, useEffect } from 'react';
import { Col, Form, Modal, Row, Switch, Input, Tabs, Tag, Divider } from "antd"
import moment from 'moment';
import uid from 'uid';

import { YearPicker } from '~/components/UI/YearPicker';
import InputCurrency, { currency2Float } from '~/components/UI/InputCurrency';
import { DatePicker } from '~/components/UI';
import Additionals from '../Additionals';

const { TextArea } = Input;
const { TabPane } = Tabs;

const HonoraryModal = ({
    isVisible,
    onClose,
    form,
    honoraries,
    setHonoraries,
    honoraryToUpdate,
    checklist,
    setChecklist,
    currentIRPFYear,
    isCreating
  }) => {
  const { getFieldDecorator, getFieldValue } = form;
  const [additionals, setAdditionals] = useState([]);

  const additionalsSum = additionals.reduce((acc, cur) => acc + cur.amount, 0);
  const total = (getFieldValue('amount') || 0) + additionalsSum;

  function setErrorHonoraryAlreadyExists(year) {
    form.setFields({
      year: {
        value: moment().set({ year }),
        errors: [new Error(`O honorário de ${year} já existe.`)]
      }
    });
  }

  function createOrUpdateHonorary() {
    form.validateFields((errors, values) => {
      if (errors) return;

      const honoraryYear = values.year.format('YYYY');
      const paidAt = values.paid_at ? values.paid_at.format('YYYY-MM-DD HH:mm:ss') : undefined;
      const currentHonorary = honoraries.find((honorary) => Number(honorary.year) === Number(honoraryYear) && !honorary.is_deleted);
      
      if (honoraryToUpdate) {
        if (currentHonorary && honoraryToUpdate.uid !== currentHonorary.uid) {
          return setErrorHonoraryAlreadyExists(honoraryYear);
        }

        const updatedHonorary = {
          ...honoraryToUpdate,
          ...values,
          year: honoraryYear,
          paid_at: paidAt,
          is_updated: true,
          additionals
        }

        setHonoraries((currentHonoraries) => {
          return currentHonoraries.map((honorary) => {
            return honorary.uid !== honoraryToUpdate.uid ? honorary : updatedHonorary
          })
        });
      } else {
        if (currentHonorary) {
          return setErrorHonoraryAlreadyExists(honoraryYear);
        }

        const newHonorary = {
          ...values,
          uid: uid(10),
          year: Number(honoraryYear),
          paid_at: paidAt,
          is_new: true,
          additionals
        }

        setHonoraries((currentHonoraries) => [...currentHonoraries, newHonorary]);

        const fullChecklist = [...checklist.list, ...checklist.newList];

        const lastYearChecklist = fullChecklist.filter((checklist) => Number(checklist.year) === (Number(honoraryYear) - 1));
        
        if (lastYearChecklist.length > 0) {
          const newChecklist = lastYearChecklist.map((checklist) => ({
            uid: uid(10),
            name: checklist.name,
            checked: false,
            year: Number(honoraryYear)
          }));

          setChecklist((currentChecklist) => ({
            ...currentChecklist,
            list: [
              ...currentChecklist.list,
              ...newChecklist
            ],
            newList: [
              ...currentChecklist.newList,
              ...newChecklist.map((checklist) => checklist.uid)
            ]
          }));
        }
      }

      onClose();
    })
  }

  React.useEffect(() => {
    if (honoraryToUpdate && honoraryToUpdate.additionals) {
      setAdditionals(honoraryToUpdate.additionals);
    } else {
      setAdditionals([]);
    }
  }, [honoraryToUpdate]);

  console.log('honoraryToUpdate', honoraryToUpdate);
  
  return (
    <Modal
      title={honoraryToUpdate ? "Editar honorário" : "Novo Honorário"}
      visible={isVisible}
      onCancel={onClose}
      onOk={createOrUpdateHonorary}
      destroyOnClose
      okText="Salvar"
      width={800}
    >
      <Form name="honorary" layout="vertical">
        <Tabs type="card">
          <TabPane tab="Honorário" key="1">
            <Divider orientation="left">Valores</Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Honorário">
                  {getFieldDecorator('amount', {
                    initialValue: honoraryToUpdate ? honoraryToUpdate.amount : undefined,
                    rules: [{ required: true, message: 'Por favor, informe o honorário' }],
                    normalize: (value) => currency2Float(value)
                  })(
                    <InputCurrency />
                  )}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Adicionais">
                  <InputCurrency
                    value={additionalsSum}
                    disabled={true}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Total">
                  <InputCurrency
                    value={total}
                    disabled={true}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Pagamento</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Data de pagamento">
                  {getFieldDecorator('paid_at', {
                    initialValue: honoraryToUpdate ? (honoraryToUpdate.paid_at ? moment(honoraryToUpdate.paid_at) : undefined) : undefined,
                  })(
                    <DatePicker style={{ width: '100%' }} />
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Quitado?">
                  {getFieldDecorator('payment_status', {
                    initialValue: honoraryToUpdate ? !!honoraryToUpdate.paid_at : false,
                    valuePropName: 'checked'
                  })(
                    <Switch />
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Informações Básicas</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Ano">
                  {getFieldDecorator('year', {
                    initialValue: honoraryToUpdate ? moment().set({ year: honoraryToUpdate.year }) : undefined,
                    rules: [{ required: true, message: 'Por favor, informe o ano do honorário' }],
                  })(
                    <YearPicker disabled={!isCreating ? (honoraryToUpdate ? Number(honoraryToUpdate.year) === currentIRPFYear : false) : false} />
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Descrição">
                  {getFieldDecorator('description', {
                    initialValue: honoraryToUpdate ? honoraryToUpdate.description : undefined,
                  })(
                    <TextArea rows={4} placeholder="Digite a descrição do honorário..." />
                  )}
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
          <TabPane tab={
            <span>Adicionais <Tag color="purple">novo</Tag></span>
          } key="2">
            <Additionals
              additionals={additionals}
              setAdditionals={setAdditionals}
            />
          </TabPane>
        </Tabs>
      </Form>
    </Modal>
  )
}

export default Form.create()(HonoraryModal)